(function () {
  const ZOOM_LEVELS = [1, 1.25, 1.5, 2];
  const RENDER_BEHIND = 1;
  const RENDER_AHEAD = 2;
  const ALLOWED_PDF_HOSTS = new Set(['cdn.shopify.com']);

  function catalogNameFromUrl(url, fallback) {
    try {
      const absolute = url.indexOf('//') === 0 ? 'https:' + url : url;
      const parsed = new URL(absolute, window.location.origin);
      const file = decodeURIComponent(parsed.pathname.split('/').pop() || '');
      const name = file.replace(/\.pdf$/i, '').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
      return name || fallback;
    } catch (error) {
      return fallback;
    }
  }

  function isAllowedMediaUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.protocol !== 'https:') return false;
      const host = parsed.hostname.toLowerCase();
      const pageHost = window.location.hostname.toLowerCase();
      return host === pageHost || ALLOWED_PDF_HOSTS.has(host);
    } catch (error) {
      return false;
    }
  }

  function isRangeIncompatibleError(error) {
    if (!error) return false;
    const name = error.name || '';
    const message = String(error.message || '').toLowerCase();
    if (name === 'UnexpectedResponseException') return true;
    if (error.status === 416) return true;
    if (message.indexOf('range') !== -1) return true;
    if (message.indexOf('content-length') !== -1) return true;
    if (message.indexOf('accept-ranges') !== -1) return true;
    return false;
  }

  function ensurePdfWorker(workerUrl) {
    if (window.__qcFlipbookWorkerReady) return;
    if (!window.pdfjsLib) {
      throw new Error('PDF.js failed to load.');
    }
    if (!workerUrl) {
      throw new Error('PDF.js worker URL is missing.');
    }
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    window.__qcFlipbookWorkerReady = true;
  }

  function cleanupPdfPage(page) {
    if (!page) return;
    try {
      page.cleanup();
    } catch (error) {
      /* Ignore cleanup races. */
    }
  }

  class ProductFlipbookElement extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.loadToken = 0;
      this.renderWindowToken = 0;
      this.currentCatalog = -1;
      this.zoomIndex = 0;
      this.zoom = 1;
      this.pageFlip = null;
      this.pdf = null;
      this.loadingTask = null;
      this.pageNodes = [];
      this.rendered = new Set();
      this.pending = new Set();
      this.objectUrls = new Map();
      this.renderTasks = new Set();
      this.viewerReady = false;
      this.viewerReadyPromise = null;
      this.catalogs = this.readCatalogs();
      this.settings = this.readSettings();
      this.onKeydown = this.onKeydown.bind(this);
      this.onFullscreenChange = this.onFullscreenChange.bind(this);
      this.onTabChange = this.onTabChange.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      document.addEventListener('section-tab:change', this.onTabChange);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.bindControls();
      this.buildShelf();
      if (!this.catalogs.length) {
        this.showError('No catalog PDF is available for this product.');
      }
      this.watchVisibility();
    }

    disconnectedCallback() {
      this.loadToken += 1;
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('section-tab:change', this.onTabChange);
      this.removeEventListener('pointerdown', this.onPointerDown);
      if (this.panelObserver) this.panelObserver.disconnect();
      this.teardownViewer();
      this.dataset.initialized = 'false';
    }

    readFlag(name, fallback) {
      const value = this.dataset[name];
      if (value === 'true') return true;
      if (value === 'false') return false;
      return fallback;
    }

    readSettings() {
      return {
        height: parseInt(this.dataset.height, 10) || 680,
        desktopPageWidth: parseInt(this.dataset.desktopPageWidth, 10) || 500,
        mobilePageWidth: parseInt(this.dataset.mobilePageWidth, 10) || 320,
        toolbar: this.readFlag('showToolbar', true),
        fullscreen: this.readFlag('showFullscreen', true),
        zoom: this.readFlag('showZoom', true),
        download: this.readFlag('enableDownload', false),
        autoplay: this.readFlag('enableAutoplay', false),
        shadows: this.readFlag('enableShadows', true),
        singlePage: this.readFlag('singlePage', false)
      };
    }

    readCatalogs() {
      const source = this.querySelector('[data-flipbook-sources]');
      if (!source) return [];
      try {
        const parsed = JSON.parse(source.textContent);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(function (item) {
          return item && typeof item.url === 'string' && item.url && isAllowedMediaUrl(item.url);
        }).map(function (item, index) {
          const cover = typeof item.cover === 'string' && item.cover && isAllowedMediaUrl(item.cover)
            ? item.cover
            : '';
          return {
            url: item.url,
            cover: cover,
            name: item.name || catalogNameFromUrl(item.url, 'Catalog ' + (index + 1))
          };
        });
      } catch (error) {
        console.error('[product-flipbook] Could not read catalog metafield data.', error);
        return [];
      }
    }

    watchVisibility() {
      const panel = this.closest('.section-tabs__tab-content');
      if (!panel) return;
      this.panelObserver = new MutationObserver(() => {
        if (!panel.classList.contains('active')) {
          this.stopAutoplay();
          return;
        }
        if (this.pageFlip) {
          window.setTimeout(() => {
            if (this.pageFlip) this.pageFlip.update();
          }, 50);
        }
      });
      this.panelObserver.observe(panel, { attributes: true, attributeFilter: ['class'] });
    }

    onTabChange(event) {
      const panel = this.closest('.section-tabs__tab-content');
      if (!panel || !event.detail || event.detail.id !== panel.id) {
        this.stopAutoplay();
        return;
      }
      if (this.pageFlip) {
        window.setTimeout(() => {
          if (this.pageFlip) this.pageFlip.update();
        }, 50);
      }
    }

    ensureViewerReady() {
      if (this.viewerReady) return Promise.resolve();
      if (this.viewerReadyPromise) return this.viewerReadyPromise;
      this.viewerReadyPromise = (async () => {
        await this.waitForLibraries();
        ensurePdfWorker(this.dataset.workerSrc);
        this.viewerReady = true;
      })().catch((error) => {
        this.viewerReadyPromise = null;
        throw error;
      });
      return this.viewerReadyPromise;
    }

    waitForLibraries() {
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const tick = () => {
          if (window.pdfjsLib && window.St && window.St.PageFlip) {
            resolve();
            return;
          }
          attempts += 1;
          if (attempts > 50) {
            reject(new Error('Catalog viewer libraries did not load.'));
            return;
          }
          window.setTimeout(tick, 100);
        };
        tick();
      });
    }

    buildShelf() {
      const covers = this.querySelector('[data-covers]');
      if (!covers) return;
      covers.setAttribute('role', 'list');
      covers.setAttribute('aria-label', 'Catalog covers');
      this.catalogs.forEach((catalog, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-flipbook__cover';
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', 'Open ' + catalog.name);
        const frame = document.createElement('span');
        frame.className = 'product-flipbook__cover-frame';
        const image = document.createElement('img');
        image.alt = '';
        image.decoding = 'async';
        image.loading = 'lazy';
        const placeholder = document.createElement('span');
        placeholder.className = 'product-flipbook__cover-placeholder';
        if (catalog.cover) {
          image.src = catalog.cover;
          placeholder.hidden = true;
        } else {
          image.hidden = true;
          placeholder.textContent = catalog.name;
        }
        frame.appendChild(image);
        frame.appendChild(placeholder);
        const name = document.createElement('span');
        name.className = 'product-flipbook__cover-name';
        name.textContent = catalog.name;
        button.appendChild(frame);
        button.appendChild(name);
        button.addEventListener('click', () => this.toggleCatalog(index));
        covers.appendChild(button);
      });
    }

    async fetchPdf(url, trackAsViewer) {
      const options = {
        url: url,
        isEvalSupported: false,
        withCredentials: false
      };
      const start = (disableAutoFetch) => window.pdfjsLib.getDocument(Object.assign({ disableAutoFetch: disableAutoFetch }, options));
      let task = start(true);
      if (trackAsViewer) this.loadingTask = task;
      try {
        const pdf = await task.promise;
        if (trackAsViewer && this.loadingTask === task) {
          this.loadingTask = null;
        }
        return { pdf: pdf, task: task };
      } catch (error) {
        try {
          task.destroy();
        } catch (destroyError) {
          /* Ignore a task that failed before it started. */
        }
        if (!isRangeIncompatibleError(error)) {
          throw error;
        }
        console.error('[product-flipbook] Range loading failed, retrying full download.', error);
        task = start(false);
        if (trackAsViewer) this.loadingTask = task;
        const pdf = await task.promise;
        if (trackAsViewer && this.loadingTask === task) {
          this.loadingTask = null;
        }
        return { pdf: pdf, task: task };
      }
    }

    toggleCatalog(index) {
      if (!this.catalogs[index]) return;
      if (this.currentCatalog === index && this.pageFlip) {
        this.closeCatalog();
        return;
      }
      const viewer = this.querySelector('[data-viewer]');
      if (viewer) viewer.hidden = false;
      this.loadCatalog(index);
      if (viewer) viewer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    closeCatalog() {
      this.stopAutoplay();
      this.loadToken += 1;
      this.teardownViewer();
      this.currentCatalog = -1;
      this.setActiveCatalog(-1);
      const viewer = this.querySelector('[data-viewer]');
      if (viewer) viewer.hidden = true;
    }

    setActiveCatalog(index) {
      this.querySelectorAll('.product-flipbook__cover').forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    bindControls() {
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      const pageInput = this.querySelector('[data-page-input]');
      const progress = this.querySelector('[data-progress]');
      const zoomIn = this.querySelector('[data-zoom-in]');
      const zoomOut = this.querySelector('[data-zoom-out]');
      const fullscreen = this.querySelector('[data-fullscreen]');
      const close = this.querySelector('[data-close]');

      if (prev) prev.addEventListener('click', () => this.prevPage());
      if (next) next.addEventListener('click', () => this.nextPage());
      if (zoomIn) zoomIn.addEventListener('click', () => this.changeZoom(1));
      if (zoomOut) zoomOut.addEventListener('click', () => this.changeZoom(-1));
      if (fullscreen) fullscreen.addEventListener('click', () => this.toggleFullscreen());
      if (close) close.addEventListener('click', () => this.exitFullscreen());
      if (pageInput) {
        pageInput.addEventListener('change', () => this.goToPage(pageInput.value));
        pageInput.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.goToPage(pageInput.value);
          }
        });
      }
      if (progress) {
        progress.addEventListener('change', () => this.goToPage(progress.value));
      }
    }

    async loadCatalog(index) {
      const catalog = this.catalogs[index];
      if (!catalog || !isAllowedMediaUrl(catalog.url)) {
        this.showError('This catalog file could not be loaded.');
        return;
      }
      if (this.currentCatalog === index && this.pageFlip) return;

      const token = ++this.loadToken;
      this.currentCatalog = index;
      this.zoomIndex = 0;
      this.zoom = 1;
      this.setActiveCatalog(index);
      this.showStatus('Loading catalog');
      this.hideError();
      this.teardownViewer();
      this.updateDownload(catalog);

      try {
        await this.ensureViewerReady();
        if (token !== this.loadToken) return;

        const opened = await this.fetchPdf(catalog.url, true);
        const pdf = opened.pdf;
        if (token !== this.loadToken) {
          try {
            await pdf.destroy();
          } catch (error) {
            /* Ignore stale PDF cleanup. */
          }
          return;
        }
        this.pdf = pdf;
        const firstPage = await pdf.getPage(1);
        if (token !== this.loadToken) {
          this.pdf = null;
          try {
            await pdf.destroy();
          } catch (error) {
            /* Ignore stale PDF cleanup. */
          }
          return;
        }
        const baseViewport = firstPage.getViewport({ scale: 1 });
        cleanupPdfPage(firstPage);
        const layout = this.getPageLayout(baseViewport.width, baseViewport.height);
        this.pagePixelWidth = layout.pixelWidth;
        this.pageNodes = this.createPages(pdf.numPages);
        this.book = this.ensureBook();
        this.applyZoom();
        this.pageFlip = new window.St.PageFlip(this.book, {
          width: layout.pageWidth,
          height: layout.pageHeight,
          size: 'fixed',
          minWidth: layout.pageWidth,
          maxWidth: layout.pageWidth,
          minHeight: layout.pageHeight,
          maxHeight: layout.pageHeight,
          drawShadow: this.settings.shadows,
          flippingTime: 700,
          usePortrait: true,
          startPage: 0,
          autoSize: true,
          maxShadowOpacity: 0.35,
          showCover: !layout.singlePage,
          mobileScrollSupport: true,
          swipeDistance: 30,
          clickEventForward: true,
          useMouseEvents: true,
          showPageCorners: true,
          disableFlipByClick: false
        });
        this.pageFlip.on('flip', (event) => {
          const pageIndex = typeof event.data === 'number' ? event.data : this.pageFlip.getCurrentPageIndex();
          this.syncControls(pageIndex);
          this.renderWindow(pageIndex);
          this.applyZoom();
          this.fitStage();
        });
        this.pageFlip.on('changeState', (event) => {
          if (event.data === 'read') this.clearPageClips();
        });
        this.pageFlip.on('changeOrientation', () => this.applyZoom());
        this.pageFlip.loadFromHTML(this.pageNodes);
        await this.renderWindow(0, token);
        if (token !== this.loadToken) return;
        this.hideStatus();
        this.syncControls(0);
        this.pageFlip.update();
        window.setTimeout(() => {
          this.applyZoom();
          this.fitStage();
        }, 60);
        if (this.settings.autoplay) this.startAutoplay();
      } catch (error) {
        if (token !== this.loadToken) return;
        this.hideStatus();
        this.showError('This catalog could not be opened.');
        console.error('[product-flipbook]', error);
      }
    }

    getPageLayout(pdfWidth, pdfHeight) {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const singlePage = this.settings.singlePage || isMobile;
      const stage = this.querySelector('[data-stage]');
      const stageWidth = stage && stage.clientWidth > 0 ? stage.clientWidth - 32 : window.innerWidth - 48;
      const spreadWidth = singlePage ? stageWidth : stageWidth / 2;
      const preferred = isMobile
        ? Math.min(this.settings.mobilePageWidth, spreadWidth)
        : Math.min(this.settings.desktopPageWidth, spreadWidth);
      const ratio = pdfHeight / pdfWidth;
      let pageWidth = Math.max(Math.floor(preferred), 140);
      let pageHeight = Math.round(pageWidth * ratio);
      const maxHeight = isMobile
        ? Math.min(window.innerHeight * 0.58, 640)
        : Math.max(this.settings.height - 120, 280);
      if (pageHeight > maxHeight) {
        pageHeight = Math.floor(maxHeight);
        pageWidth = Math.max(Math.floor(pageHeight / ratio), 140);
      }
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      return {
        singlePage: singlePage,
        pageWidth: pageWidth,
        pageHeight: pageHeight,
        pixelWidth: Math.min(Math.round(pageWidth * pixelRatio), 1400)
      };
    }

    createPages(count) {
      const pages = [];
      for (let index = 0; index < count; index += 1) {
        const page = document.createElement('div');
        page.className = 'product-flipbook__page';
        page.dataset.page = String(index + 1);
        if (index === 0) page.dataset.density = 'hard';
        const image = document.createElement('img');
        image.alt = 'Catalog page ' + (index + 1);
        image.draggable = false;
        image.decoding = 'async';
        page.appendChild(image);
        pages.push(page);
      }
      return pages;
    }

    ensureBook() {
      let book = this.querySelector('[data-book]');
      const stage = this.querySelector('[data-stage]');
      if (!book && stage) {
        book = document.createElement('div');
        book.className = 'product-flipbook__book';
        book.setAttribute('data-book', '');
        stage.appendChild(book);
      }
      if (book) book.replaceChildren();
      return book;
    }

    async renderWindow(pageIndex, token = this.loadToken) {
      if (!this.pdf || token !== this.loadToken) return;
      const windowToken = ++this.renderWindowToken;
      const keep = [];
      for (let index = pageIndex - RENDER_BEHIND; index <= pageIndex + RENDER_AHEAD; index += 1) {
        if (index >= 0 && index < this.pdf.numPages) keep.push(index + 1);
      }
      await this.renderPage(pageIndex + 1, token);
      if (token !== this.loadToken || windowToken !== this.renderWindowToken) return;
      const remaining = keep.filter((pageNumber) => pageNumber !== pageIndex + 1);
      await Promise.all(remaining.map((pageNumber) => this.renderPage(pageNumber, token)));
      if (token !== this.loadToken || windowToken !== this.renderWindowToken) return;
      this.releaseFarPages(new Set(keep));
    }

    async renderPage(pageNumber, token = this.loadToken) {
      if (!this.pdf || token !== this.loadToken || this.rendered.has(pageNumber) || this.pending.has(pageNumber)) {
        return;
      }
      const pageNode = this.pageNodes[pageNumber - 1];
      if (!pageNode) return;
      this.pending.add(pageNumber);
      let page = null;
      try {
        page = await this.pdf.getPage(pageNumber);
        if (token !== this.loadToken) return;
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({
          scale: Math.max(this.pagePixelWidth / base.width, 0.5)
        });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const context = canvas.getContext('2d', { alpha: false });
        const task = page.render({ canvasContext: context, viewport: viewport });
        this.renderTasks.add(task);
        try {
          await task.promise;
        } finally {
          this.renderTasks.delete(task);
        }
        if (token !== this.loadToken) return;
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
        canvas.width = 1;
        canvas.height = 1;
        if (!blob || token !== this.loadToken) return;
        const url = URL.createObjectURL(blob);
        const image = pageNode.querySelector('img');
        this.revokePage(pageNumber);
        if (image) image.src = url;
        this.objectUrls.set(pageNumber, url);
        this.rendered.add(pageNumber);
      } catch (error) {
        if (error && error.name === 'RenderingCancelledException') return;
        console.error('[product-flipbook] Page ' + pageNumber + ' failed to render.', error);
      } finally {
        cleanupPdfPage(page);
        this.pending.delete(pageNumber);
      }
    }

    releaseFarPages(keep) {
      Array.from(this.rendered).forEach((pageNumber) => {
        if (keep.has(pageNumber)) return;
        this.revokePage(pageNumber);
        const image = this.pageNodes[pageNumber - 1] && this.pageNodes[pageNumber - 1].querySelector('img');
        if (image) image.removeAttribute('src');
        this.rendered.delete(pageNumber);
      });
    }

    revokePage(pageNumber) {
      const url = this.objectUrls.get(pageNumber);
      if (!url) return;
      URL.revokeObjectURL(url);
      this.objectUrls.delete(pageNumber);
    }

    teardownViewer() {
      this.stopAutoplay();
      this.renderWindowToken += 1;
      for (const task of this.renderTasks) {
        try {
          task.cancel();
        } catch (error) {
          /* Ignore completed/cancelled tasks. */
        }
      }
      this.renderTasks.clear();
      this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.objectUrls.clear();
      this.rendered.clear();
      this.pending.clear();
      this.pageNodes = [];
      if (this.loadingTask) {
        try {
          this.loadingTask.destroy();
        } catch (error) {
          /* Ignore destroy races while switching catalogs. */
        }
        this.loadingTask = null;
      }
      if (this.pdf) {
        try {
          this.pdf.destroy();
        } catch (error) {
          /* Ignore destroy races while switching catalogs. */
        }
        this.pdf = null;
      }
      if (this.pageFlip) {
        const flip = this.pageFlip;
        this.pageFlip = null;
        try {
          flip.destroy();
        } catch (error) {
          /* PageFlip removes its mount node during destroy. */
        }
      }
      this.book = null;
    }

    syncControls(pageIndex) {
      if (!this.pageFlip) return;
      const count = this.pageFlip.getPageCount();
      const page = pageIndex + 1;
      const input = this.querySelector('[data-page-input]');
      const total = this.querySelector('[data-page-total]');
      const progress = this.querySelector('[data-progress]');
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (input && document.activeElement !== input) {
        input.value = String(page);
        input.max = String(count);
      }
      if (total) total.textContent = 'of ' + count;
      if (progress) {
        progress.max = String(count);
        progress.value = String(page);
        progress.setAttribute('aria-valuemax', String(count));
        progress.setAttribute('aria-valuenow', String(page));
      }
      if (prev) prev.disabled = pageIndex <= 0;
      if (next) next.disabled = pageIndex >= count - 1;
      this.setLiveMessage('Page ' + page + ' of ' + count);
    }

    setLiveMessage(message) {
      let live = this.querySelector('[data-live]');
      if (!live) {
        live = document.createElement('div');
        live.className = 'product-flipbook__sr';
        live.setAttribute('data-live', '');
        live.setAttribute('aria-live', 'polite');
        this.appendChild(live);
      }
      live.textContent = message;
    }

    showStatus(message) {
      const status = this.querySelector('[data-status]');
      if (!status) return;
      status.hidden = false;
      status.textContent = message;
    }

    hideStatus() {
      const status = this.querySelector('[data-status]');
      if (status) status.hidden = true;
    }

    showError(message) {
      this.hideStatus();
      const error = this.querySelector('[data-error]');
      if (!error) return;
      error.hidden = false;
      error.textContent = message;
    }

    hideError() {
      const error = this.querySelector('[data-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
    }

    updateDownload(catalog) {
      const download = this.querySelector('[data-download]');
      if (!download || !this.settings.download) return;
      download.href = catalog.url;
      download.setAttribute('download', '');
      download.target = '_blank';
      download.rel = 'noopener';
    }

    isActiveViewer() {
      if (!this.pageFlip) return false;
      const panel = this.closest('.section-tabs__tab-content');
      return !panel || panel.classList.contains('active');
    }

    prevPage() {
      if (!this.pageFlip) return;
      this.stopAutoplay();
      this.pageFlip.flipPrev('bottom');
    }

    nextPage() {
      if (!this.pageFlip) return;
      this.stopAutoplay();
      this.pageFlip.flipNext('bottom');
    }

    goToPage(value) {
      if (!this.pageFlip) return;
      this.stopAutoplay();
      const count = this.pageFlip.getPageCount();
      const page = Math.min(Math.max(parseInt(value, 10) || 1, 1), count);
      const token = this.loadToken;
      this.pageFlip.turnToPage(page - 1);
      this.renderWindow(page - 1, token);
      this.syncControls(page - 1);
    }

    changeZoom(direction) {
      const next = Math.min(Math.max(this.zoomIndex + direction, 0), ZOOM_LEVELS.length - 1);
      if (next === this.zoomIndex) return;
      this.zoomIndex = next;
      this.zoom = ZOOM_LEVELS[next];
      this.applyZoom();
    }

    singlePageShift() {
      if (!this.pageFlip || this.pageFlip.getOrientation() === 'portrait') return 0;
      const index = this.pageFlip.getCurrentPageIndex();
      const count = this.pageFlip.getPageCount();
      if (count <= 1) return 25;
      if (index === 0) return -25;
      if (index === count - 1 && count % 2 === 0) return 25;
      return 0;
    }

    fitStage() {
      const stage = this.querySelector('[data-stage]');
      if (!stage || !this.book) return;
      const height = this.book.offsetHeight;
      if (height > 0) stage.style.minHeight = height + 8 + 'px';
    }

    clearPageClips() {
      if (!this.book) return;
      this.book.querySelectorAll('.stf__item').forEach((page) => {
        page.style.clipPath = '';
        page.style.webkitClipPath = '';
      });
    }

    applyZoom() {
      if (!this.book) this.book = this.querySelector('[data-book]');
      if (!this.book) return;
      const shift = this.singlePageShift();
      const parts = [];
      if (shift) parts.push('translateX(' + shift + '%)');
      if (this.zoom !== 1) parts.push('scale(' + this.zoom + ')');
      this.book.style.transform = parts.join(' ');
      const stage = this.querySelector('[data-stage]');
      if (stage) {
        const extra = this.zoom > 1 ? this.book.offsetHeight * (this.zoom - 1) : 0;
        stage.style.paddingBottom = extra ? extra + 24 + 'px' : '';
      }
      const label = this.querySelector('[data-zoom-value]');
      if (label) label.textContent = Math.round(this.zoom * 100) + '%';
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.settings.autoplay) return;
      this.autoplayTimer = window.setInterval(() => {
        if (!this.pageFlip || !this.isActiveViewer()) return;
        if (this.pageFlip.getCurrentPageIndex() >= this.pageFlip.getPageCount() - 1) {
          this.stopAutoplay();
          return;
        }
        this.pageFlip.flipNext('bottom');
      }, 4500);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    onPointerDown() {
      this.stopAutoplay();
    }

    onKeydown(event) {
      if (!this.isActiveViewer()) return;
      if (event.key === 'Escape') {
        this.exitFullscreen();
        return;
      }
      const target = event.target;
      if (target && (target.closest('input, textarea, select') || target.isContentEditable)) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.nextPage();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prevPage();
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goToPage(1);
      } else if (event.key === 'End' && this.pageFlip) {
        event.preventDefault();
        this.goToPage(this.pageFlip.getPageCount());
      }
    }

    async toggleFullscreen() {
      const active = document.fullscreenElement === this || this.classList.contains('is-fullscreen');
      if (active) {
        await this.exitFullscreen();
        return;
      }
      try {
        await this.requestFullscreen();
      } catch (error) {
        this.classList.add('is-fullscreen');
      }
      this.syncFullscreenButton();
      window.setTimeout(() => {
        if (this.pageFlip) this.pageFlip.update();
      }, 200);
    }

    async exitFullscreen() {
      if (document.fullscreenElement) {
        try {
          await document.exitFullscreen();
        } catch (error) {
          /* Fullscreen can already be closed by the browser. */
        }
      }
      this.classList.remove('is-fullscreen');
      this.syncFullscreenButton();
      window.setTimeout(() => {
        if (this.pageFlip) this.pageFlip.update();
      }, 200);
    }

    onFullscreenChange() {
      if (document.fullscreenElement !== this) this.classList.remove('is-fullscreen');
      this.syncFullscreenButton();
    }

    syncFullscreenButton() {
      const button = this.querySelector('[data-fullscreen]');
      if (!button) return;
      const active = document.fullscreenElement === this || this.classList.contains('is-fullscreen');
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? 'Exit fullscreen' : 'Enter fullscreen');
    }
  }

  if (!customElements.get('product-flipbook')) {
    customElements.define('product-flipbook', ProductFlipbookElement);
  }
})();
